import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import ReactPixel from "react-facebook-pixel";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import {
  kirkos_logo_white_yellow,
  car_sign_in,
  icon_normal,
} from "../../../assets/images";
import { CustomInput, ErrorModal, Turnstile } from "../../../components";
import { TurnstileRef } from "../../../components/Turnstile";
import { useForm } from "react-hook-form";
import rules from "../../../rules";
import { useMutation } from "@tanstack/react-query";
import { trackEvent } from "../../../services/events";
import { setTokens } from "../../../utils/tokenStorage";
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";
import { LoginData } from "../../../types";
import { useNavigate, useLocation, Link } from "react-router-dom";
import "./styles.css";
import "./SignInScreen.css";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../../Redux/Auth/authSlice";
import { ERROR_MESSAGES } from "../../../utils/messages";
import SEO from "../../../components/SEO";
import DraggableWhatsAppIcon from "../../../components/CustomerDashBoard/DraggableWhatsAppIcon";
const CustomerSignUpFormBoot = () => {
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showCredentialErrorModal, setShowCredentialErrorModal] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileRef>(null);
  const loginFormDataRef = useRef<any>(null);
  const cachedTokenRef = useRef<{ token: string; timestamp: number } | null>(null);

  // ── Page load tracking ─────────────────────────────────────────────────────
  useEffect(() => {
    trackEvent({ event_name: "Kirkos_SignIn_Page_View", event_category: "Page_View", module: "Login", payload: { source: "web" } }).catch(() => { });
    if (process.env.REACT_APP_META_PIXEL_ID) {
      try { ReactPixel.track("ViewContent", { content_name: "SignIn Page", content_category: "Auth" }); } catch (_) { }
    }
  }, []);
  // const [isChecked, setChecked] = useState(false);

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      phoneNo: "",
      password: "",
    },
  });

  let access_token: string = "";
  let refresh_token: string = "";

  const resolveEventName = (value: string | { web?: string; app?: string }) => {
    if (typeof value === "string") return value;
    const runtime = process.env.REACT_APP_RUNTIME?.toLowerCase();
    const isApp = runtime === "app";
    return isApp ? value.app ?? value.web ?? "" : value.web ?? value.app ?? "";
  };

  const handleLogin = useMutation({
    mutationFn: (userData: LoginData) =>
      axiosInstance.post(API_URL.LOGIN, userData),
    onSuccess: async (successResponse: any) => {
      await trackEvent({
        event_name: { web: "Kirkos_SignIn", app: "Kirkos_SignIn_App" },
        event_category: "SignIn",
        module: "Login",
        payload: {
          status: "success",
          message: successResponse?.data?.message ?? "",
        },
      });

      console.log("successResponse", successResponse);

      if (successResponse.status && successResponse?.data?.message) {
        alert(successResponse?.data?.message);
      }
      access_token = successResponse?.data?.access_token;
      refresh_token = successResponse?.data?.refresh_token;
      await setTokens(access_token, refresh_token);
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      saveTokenToLocalStorage();
      dispatch(loginSuccess());
      // Check if there's a pending booking from the homepage "Book Now" flow
      let pendingBooking = (location.state as any)?.pendingBooking;
      const fromHomepage = sessionStorage.getItem("pendingBookingFromHomepage") === "true";

      // Extract UTM/campaign parameters from location state
      const campaignId = (location.state as any)?.campaignId;
      const utmParams = Object.keys(location.state || {}).reduce((acc: any, key: string) => {
        if (key.startsWith('utm_') || ['placement', 'adset_id', 'ad_id'].includes(key)) {
          acc[key] = (location.state as any)[key];
        }
        return acc;
      }, {});

      // Fallback to sessionStorage if state was lost during redirects (common in signup flow)
      if (!pendingBooking && fromHomepage) {
        try {
          const storedData = sessionStorage.getItem("pendingBookingData");
          if (storedData) {
            pendingBooking = JSON.parse(storedData);
          }
        } catch (e) {
          console.error("Failed to parse pending booking data", e);
        }
      }

      // Final check and hydration: Ensure dates are real Date objects if they were serialized to strings
      if (pendingBooking && fromHomepage && pendingBooking.searchData) {
        if (pendingBooking.searchData.pickupDate && typeof pendingBooking.searchData.pickupDate === "string") {
          pendingBooking.searchData.pickupDate = new Date(pendingBooking.searchData.pickupDate);
        }
        if (pendingBooking.searchData.dropOffDate && typeof pendingBooking.searchData.dropOffDate === "string") {
          pendingBooking.searchData.dropOffDate = new Date(pendingBooking.searchData.dropOffDate);
        }
      }

      if (pendingBooking && fromHomepage) {
        // Consume the one-time flag and data
        sessionStorage.removeItem("pendingBookingFromHomepage");
        sessionStorage.removeItem("pendingBookingData");
        navigate("/customer/unified-booking", {
          state: {
            ...pendingBooking,
            ...(campaignId && { campaignId }),
            ...utmParams,
            fromLandingCampaign: true,
          }
        });
      } else {
        // Normal flow: clear any stale search data so dashboard starts fresh
        sessionStorage.removeItem("customerDashboardSearchData");
        sessionStorage.removeItem("pendingBookingFromHomepage");
        sessionStorage.removeItem("pendingBookingData");
        navigate("/customer/myaccount");
      }
    },

    onError: async (error: any) => {
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      loginFormDataRef.current = null;
      cachedTokenRef.current = null; // Clear cache on error

      const eventNameInput = {
        web: "Kirkose_SignInFailed",
        app: "Kirkose_SignInFailed_App",
      };
      console.log("trackEvent event_name:", resolveEventName(eventNameInput));
      await trackEvent({
        event_name: {
          web: "Kirkose_SignInFailed",
          app: "Kirkose_SignInFailed_App",
        },
        event_category: "SignIn",
        module: "Login",
        payload: {
          status: "failed",
          message: error?.data?.message ?? error?.message ?? "Unknown error",
        },
      });
      if (
        error?.data?.message ===
        "You are not registered with this mobile number"
      ) {
        setErrorMessage(error?.data?.message || "You are not registered with this mobile number");
        setShowErrorModal(true);
      } else if (
        error?.data?.message === "The credentials you entered are invalid"
      ) {
        setErrorMessage(error?.data?.message || "The credentials you entered are invalid");
        setShowCredentialErrorModal(true);
      } else {
        setErrorMessage(error?.data?.message || ERROR_MESSAGES.DEFAULT_ERROR);
        setShowErrorModal(true);
      }
    },
  });

  const saveTokenToLocalStorage = () => {
    if (access_token !== null && refresh_token !== null) {
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
    }
  };

  // const handleCheckBoxChange = () => {
  //   setChecked(!isChecked);
  //   if (isChecked) {
  //     localStorage.removeItem("access_token");
  //     localStorage.removeItem("refresh_token");
  //   } else {
  //     saveTokenToLocalStorage();
  //   }
  // };

  const handleTurnstileSuccess = (token: string) => {
    setTurnstileToken(token);
    
    // Cache token for 5 minutes to skip re-verification on subsequent login attempts
    cachedTokenRef.current = { token, timestamp: Date.now() };

    const data = loginFormDataRef.current;
    if (data) {
      const userData: LoginData = {
        username: data.phoneNo,
        password: data.password,
        userType: "CONSUMER",
        turnstile_response: token,

      };

      trackEvent({ event_name: "Kirkos_SignIn_Submit_Attempt", event_category: "SignIn", module: "Login", payload: { phone: data.phoneNo } }).catch(() => { });
      if (!(window as any).fbq && process.env.REACT_APP_META_PIXEL_ID) {
        ReactPixel.init(process.env.REACT_APP_META_PIXEL_ID);
      }
      ReactPixel.track("Lead", {
        content_type: "Login",
        content_name: "Customer Login",
        content_category: "Customer",
        username: data.phoneNo,
      });

      handleLogin.mutate(userData);
      loginFormDataRef.current = null;
    }
  };

  const onHandleSubmit = (data: any) => {
    if (!data?.phoneNo) return;

    loginFormDataRef.current = data;

    // Check if we have a cached token that's less than 5 minutes old
    const now = Date.now();
    if (cachedTokenRef.current && (now - cachedTokenRef.current.timestamp < 5 * 60 * 1000)) {
      // Reuse cached token - this skips Cloudflare verification calls
      handleTurnstileSuccess(cachedTokenRef.current.token);
      return;
    }

    // Reset the widget first, then execute after a tick to let Cloudflare re-initialize
    setTurnstileToken(null);
    turnstileRef.current?.reset();
    setTimeout(() => {
      turnstileRef.current?.execute();
    }, 50);
  };

  const onHandleError = (errors: any) => {
    const values = getValues();
    const isPhoneEmpty = !values.phoneNo;
    const isPasswordEmpty = !values.password;

    if (isPhoneEmpty && isPasswordEmpty) {
      toast.error("Please enter your mobile number and password to sign in.");
      return;
    }

    if (isPhoneEmpty) return;

    if (isPasswordEmpty) {
      toast.error("Please enter your password to sign in.");
      return;
    }

    if (errors?.phoneNo) {
      toast.error("Please enter a valid 9-digit mobile number.");
      return;
    }

    const firstError = Object.values(errors)[0] as any;
    if (firstError) {
      toast.error(firstError.message || "Please check your login credentials and try again.");
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
    trackEvent({ event_name: "Kirkos_SignIn_Password_Toggle", event_category: "UI_Interaction", module: "Login", payload: { revealed: !showPassword } }).catch(() => { });
  };

  const handleForgotPasswordClick = async () => {
    await trackEvent({
      event_name: {
        web: "Kirkos_ForgotPassword",
        app: "Kirkos_ForgotPassword_App",
      },
      event_category: "SignIn",
      module: "Login",
      payload: "{}",
    });
  };

  const signinSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Sign In | Kirkos Car Rental",
    "description": "Log in to your Kirkos account to manage your car rental bookings."
  };

  return (
    <div className="customer-signin-page">
      <SEO
        title="Login | Access Your Kirkos Account"
        description="Sign in to your Kirkos account to manage your car rental bookings, view history, and update your profile."
        keywords="kirkos login, car rental account, manage bookings, car hire login"
        schemaData={signinSchema}
      />
      <div className="customer-signin-shell">
        <div className="customer-signin-brand-panel">
          <div className="customer-signin-brand-topbar">
            <Link to="/" className="customer-signin-logo-link" aria-label="Kirkos home">
              <img
                src={kirkos_logo_white_yellow}
                alt="Kirkos"
                className="customer-signin-logo"
              />
            </Link>
            <div className="customer-signin-brand-links">
              <Link to="/" className="customer-signin-brand-link">
                Home
              </Link>
              <Link to="/signup" className="customer-signin-brand-link"
                onClick={() => trackEvent({ event_name: "Kirkos_SignIn_Nav_Register_Click", event_category: "Navigation", module: "Login", payload: {} }).catch(() => { })}
              >
                Register
              </Link>
            </div>
          </div>

          <div className="customer-signin-brand-content">
            <span className="customer-signin-eyebrow">Customer access</span>
            <h1 className="customer-signin-brand-title">
              Premium journeys start with a more polished sign-in.
            </h1>
            <p className="customer-signin-brand-copy">
              Access your bookings, saved details, and upcoming trips through a
              calmer Kirkos experience built around confidence, speed, and
              premium support.
            </p>

            <div className="customer-signin-brand-highlights">
              <div className="customer-signin-highlight-card">
                <span className="customer-signin-highlight-value">24/7</span>
                <span className="customer-signin-highlight-label">
                  Trip support when plans shift
                </span>
              </div>
              <div className="customer-signin-highlight-card">
                <span className="customer-signin-highlight-value">Fast</span>
                <span className="customer-signin-highlight-label">
                  Booking access across your account
                </span>
              </div>
              <div className="customer-signin-highlight-card">
                <span className="customer-signin-highlight-value">UAE</span>
                <span className="customer-signin-highlight-label">
                  Trusted mobility experience
                </span>
              </div>
            </div>

            <div className="customer-signin-car-stage">
              <div className="customer-signin-car-glow" />
              <img
                src={car_sign_in}
                className="customer-signin-car-image"
                alt="Kirkos premium rental car"
              />
            </div>
          </div>
        </div>

        <div className="customer-signin-card">
          <div className="customer-signin-card-header">
            <div className="customer-signin-card-icon-wrap">
              <img
                src={icon_normal}
                className="customer-signin-card-icon"
                alt="Customer account"
              />
            </div>
            <span className="customer-signin-card-kicker">Kirkos account</span>
            <h2 className="customer-signin-heading">Welcome back</h2>
            <p className="customer-signin-subcopy">
              Sign in to continue your rental journey, manage bookings, and
              keep your travel details ready for the next trip.
            </p>
            <div className="customer-signin-links-row">
              <span className="customer-signin-links-label">
                New to Kirkos?
              </span>
              <Link to="/signup" className="customer-signin-inline-link">
                Create account
              </Link>
              <Link to="/" className="customer-signin-inline-link">
                Back to home
              </Link>
            </div>
          </div>

          <div className="customer-signin-form-area">
            <div className="customer-signin-field-block">
              <label className="customer-signin-label">
                Mobile number
              </label>
              <div
                className="customer-signin-input-shell"
                onClick={() => {
                  phoneInputRef.current?.focus();
                  trackEvent({ event_name: "Kirkos_SignIn_Phone_Field_Focus", event_category: "UI_Interaction", module: "Login", payload: {} }).catch(() => { });
                }}
              >
                <span className="customer-signin-country-code">
                  <img
                    src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIxMiIgdmlld0JveD0iMCAwIDYwMCAzMDAiPgogIDxyZWN0IHdpZHRoPSI2MDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjMDA3MzJmIi8+CiAgPHJlY3Qgd2lkdGg9IjYwMCIgaGVpZ2h0PSIxMDAiIHk9IjEwMCIgZmlsbD0iI2ZmZiIvPgogIDxyZWN0IHdpZHRoPSI2MDAiIGhlaWdodD0iMTAwIiB5PSIyMDAiIGZpbGw9IiMwMDAiLz4KICA8cmVjdCB3aWR0aD0iMTUwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2ZmMDAwMCIvPgo8L3N2Zz4="
                    alt="UAE"
                    className="customer-signin-flag"
                  />
                  +971
                </span>
                <CustomInput
                  inputName="phoneNo"
                  inputType="tel"
                  className="customer-signin-input"
                  placeholder="Enter your mobile number"
                  editable={true}
                  maxLength={9}
                  onInputChange={(value: string) => { }}
                  control={control}
                  rules={{ ...rules.AuthRules.phone_number, required: false }}
                  autoFocus={true}
                  inputRef={phoneInputRef}
                />
              </div>
            </div>

            <div className="customer-signin-field-block">
              <label className="customer-signin-label">Password</label>
              <div
                className="customer-signin-input-shell"
                onClick={() => {
                  passwordInputRef.current?.focus();
                  trackEvent({ event_name: "Kirkos_SignIn_Password_Field_Focus", event_category: "UI_Interaction", module: "Login", payload: {} }).catch(() => { });
                }}
              >
                <CustomInput
                  inputName="password"
                  inputType={showPassword ? "text" : "password"}
                  className="customer-signin-input"
                  placeholder="Enter your password"
                  editable={true}
                  onInputChange={(value: string) => { }}
                  control={control}
                  rules={{ required: true }}
                  inputRef={passwordInputRef}
                />
                <button
                  type="button"
                  className="customer-signin-password-toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePasswordVisibility();
                  }}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeSlash /> : <Eye />}
                </button>
              </div>
            </div>

            <div className="customer-signin-utility-row">
              <span className="customer-signin-utility-copy">
                Secure access to your saved trips and preferences.
              </span>
              <Link
                to="/forgotpassword"
                className="customer-signin-forgot-link"
                onClick={handleForgotPasswordClick}
              >
                Forgot Password?
              </Link>
            </div>

            <div className="customer-signin-turnstile-container">
              <Turnstile
                ref={turnstileRef}
                sitekey={process.env.REACT_APP_TURNSTILE_SITEKEY || ""}
                appearance="interaction-only"
                execution="execute"
                onSuccess={handleTurnstileSuccess}
                onError={() => {
                  setTurnstileToken(null);
                  loginFormDataRef.current = null;
                  toast.error("Security verification failed. Please try again.");
                }}
                onExpire={() => {
                  setTurnstileToken(null);
                  loginFormDataRef.current = null;
                  toast.warning("Security verification expired. Please verify again.");
                }}
              />
            </div>

            <button
              className="customer-signin-submit"
              onClick={handleSubmit(onHandleSubmit, onHandleError)}
            >
              Login
            </button>

            <div className="customer-signin-support-strip">
              <span className="customer-signin-support-title">
                Need help with access?
              </span>
              <span className="customer-signin-support-copy">
                Our team can help you recover your account and continue your
                booking without disruption.
              </span>
            </div>
          </div>

          <ErrorModal
            isOpen={showErrorModal}
            onClose={() => setShowErrorModal(false)}
            message={errorMessage}
          />

          <ErrorModal
            isOpen={showCredentialErrorModal}
            onClose={() => setShowCredentialErrorModal(false)}
            message={errorMessage}
          />
        </div>
      </div>
      <DraggableWhatsAppIcon />
    </div>
  );
};

export default CustomerSignUpFormBoot;
